import { ReactNode, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Divider, Drawer, Grid, List, ListItem, Stack, Typography, Button, Chip, Switch, FormControlLabel } from '@mui/material';
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import SimpleBar from 'components/third-party/SimpleBar';
import { Add, Slash, ShoppingCart } from 'iconsax-react';
import { ThemeMode } from 'types/config';
import serverCall from 'api/serverCall';
import api from 'api/api';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { OrderRequest, OrderRequestStatus } from 'types/custom/order';
import Amount from 'components/common/Amount';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { useWebsocketContext } from 'contexts/WebSocketContext';
import Avatar from 'components/@extended/Avatar';
import Alert from 'components/alert/CommonAlert';
import { formatDateCommon, formatNumberToCurrency } from 'utils/formatters';
import timeAgo from 'utils/timeAgo';
import { CurrencyType } from 'enum/currency';
import { useFirebaseContext } from 'contexts/FirebaseContext';
import { SimulationSource } from 'types/custom/simulation';
import ChangeOrderQuantityModal from './ChangeOrderQuantity';
import EditIcon from '@mui/icons-material/Edit';

const StyledGridOverlay = styled(Stack)(({ theme }) => ({
  height: '400px',
  '& .ant-empty-img-1': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary[200] : theme.palette.secondary[400]
  },
  '& .ant-empty-img-2': {
    fill: theme.palette.secondary.light
  },
  '& .ant-empty-img-3': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary[400] : theme.palette.secondary[200]
  },
  '& .ant-empty-img-4': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary.dark : theme.palette.common.white
  },
  '& .ant-empty-img-5': {
    fillOpacity: theme.palette.mode === ThemeMode.DARK ? '0.09' : '0.95',
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary.darker : theme.palette.secondary.light
  }
}));

type Props = {
  open: boolean;
  setOpen: (val: boolean) => void;
  setOrderRequestCount: (val: number) => void;
};

const OrderRequestDrawer: React.FC<Props> = ({ open, setOpen, setOrderRequestCount }) => {
  const { orderRequestStatus, orderStatusChanged, liveTicks } = useWebsocketContext();
  const [orders, setOrders] = useState<OrderRequest[]>([]);
  const [, setPriceRefreshKey] = useState(0);
  const [mtfToggle, setMtfToggle] = useState<Record<number, boolean>>({});
  const [isOpenCancelOrderConfirmation, setIsOpenCancelOrderConfirmation] = useState<boolean>(false);
  const { user } = useFirebaseContext();
  const permissions = user?.permissions;
  const hasApproveOrderPermission = permissions?.includes('canApproveOrderRequest');
  const [changeQtyState, setChangeQtyState] = useState({
    open: false,
    orderId: '',
    userId: '',
    currentQty: 0
  });

  const handleToggle = () => {
    setOpen(!open);
  };

  const fetchAllOrderRequests = () => {
    serverCall.get(api.getAllPendingOrders(), (response, error) => {
      if (error) {
        console.error(error);
      } else {
        const fetched: OrderRequest[] = response.orders;
        setOrders(fetched);
        setOrderRequestCount(response.totalCount ?? 0);
        setMtfToggle(
          fetched.reduce((acc: Record<number, boolean>, o: OrderRequest) => {
            acc[o.requestId] = o.useMTF ?? false;
            return acc;
          }, {})
        );
      }
    });
  };

  useEffect(() => {
    fetchAllOrderRequests();
  }, [orderRequestStatus, orderStatusChanged]);

  useEffect(() => {
    if (open) fetchAllOrderRequests();
  }, [open]);

  // Live LTP is stored on a ref (no re-renders), so periodically refresh while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => setPriceRefreshKey((key) => key + 1), 1000);
    return () => clearInterval(interval);
  }, [open]);

  const handleClearAll = () => {
    serverCall.put(api.clearAllOrders(SimulationSource.LIVE), {}, (response, error) => {
      if (error) {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Error declining all orders`,
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      } else {
        fetchAllOrderRequests();
        setOrderRequestCount(0);
        setIsOpenCancelOrderConfirmation(false);
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `All orders cancelled successfully`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      }
    });
  };

  const handleDeclineOrder = (requestId: string, orderId: string, userId: string) => {
    serverCall.put(api.declineOrderRequest(requestId, orderId, userId), {}, (response, error) => {
      if (error) {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Error declining order request ${requestId}`,
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      } else {
        fetchAllOrderRequests();
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Order request ${requestId} declined successfully`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      }
    });
  };

  const handleApproveOrder = (orderId: string, useMTF: boolean) => {
    serverCall.put(api.approveOrderRequest(orderId, SimulationSource.LIVE), { useMTF }, (response, error) => {
      if (error) {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Error approving order ${orderId}`,
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      } else {
        fetchAllOrderRequests();
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Order ${orderId} approved successfully`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      }
    });
  };

  const handleCloseModal = () => {
    setIsOpenCancelOrderConfirmation(false);
  };

  const alertContent: ReactNode = (
    <Stack alignItems="center" spacing={3.5}>
      <Avatar color="warning" sx={{ width: 72, height: 72, fontSize: '1.75rem' }}>
        <Slash variant="Bold" />
      </Avatar>
      <Stack spacing={2}>
        <Typography variant="h4" align="center">
          Confirm Order Clearing
        </Typography>
        <Typography align="center">Are you sure you want to clear all orders?</Typography>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ width: 1 }}>
        <Button fullWidth onClick={handleCloseModal} color="secondary" variant="outlined">
          No, Keep Orders
        </Button>
        <Button fullWidth color="primary" variant="contained" onClick={handleClearAll} autoFocus>
          Yes, Clear Orders
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <>
      {isOpenCancelOrderConfirmation && (
        <Alert open={isOpenCancelOrderConfirmation} handleClose={handleCloseModal} content={alertContent} />
      )}

      {changeQtyState.open && (
        <ChangeOrderQuantityModal state={changeQtyState} setState={setChangeQtyState} refetchSimulation={fetchAllOrderRequests} />
      )}

      <Drawer sx={{ zIndex: 2001 }} anchor="right" onClose={handleToggle} open={open} PaperProps={{ sx: { width: { xs: 350, sm: 474 } } }}>
        {open && (
          <MainCard content={false} sx={{ border: 'none', borderRadius: 0, height: '100vh' }}>
            <SimpleBar
              sx={{
                '& .simplebar-content': {
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                  <Typography variant="h5">Order Requests</Typography>
                  <IconButton color="secondary" sx={{ p: 0 }} onClick={handleToggle}>
                    <Add size={28} style={{ transform: 'rotate(45deg)' }} />
                  </IconButton>
                </Stack>
                <Divider sx={{ mt: 1.5, mb: 2 }} />
                {orders.length > 0 && (
                  <Box sx={{ mb: 2 }} justifyContent="end" display="flex">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setIsOpenCancelOrderConfirmation(true);
                      }}
                      startIcon={<CancelIcon />}
                    >
                      Clear All Orders
                    </Button>
                  </Box>
                )}
                <Grid>
                  {orders.length === 0 ? (
                    <StyledGridOverlay alignItems="center" justifyContent="center" spacing={1}>
                      <svg width="120" height="100" viewBox="0 0 184 152" aria-hidden focusable="false">
                        <g fill="none" fillRule="evenodd">
                          <g transform="translate(24 31.67)">
                            <ellipse className="ant-empty-img-5" cx="67.797" cy="106.89" rx="67.797" ry="12.668" />
                            <path
                              className="ant-empty-img-1"
                              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
                            />
                            <path
                              className="ant-empty-img-2"
                              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
                            />
                            <path
                              className="ant-empty-img-3"
                              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
                            />
                          </g>
                          <path
                            className="ant-empty-img-3"
                            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
                          />
                          <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
                            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
                            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
                          </g>
                        </g>
                      </svg>
                      <Typography align="center">Oops! No orders requests found!.</Typography>
                    </StyledGridOverlay>
                  ) : (
                    <List disablePadding sx={{ '& .MuiListItem-root': { py: 1.5, borderBottom: '1px solid', borderColor: 'divider' } }}>
                      {orders
                        .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
                        .map((order) => (
                          <ListItem
                            key={order.requestId}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              '&:hover': {
                                backgroundColor: 'action.hover'
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                              <Typography variant="subtitle2">
                                {order?.firstName + ' ' + order?.lastName + ' - ' + (order?.stockId ?? '')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.createdOn
                                  ? timeAgo(order.createdOn) + ', ' + formatDateCommon(order.createdOn, 'dd-MMM-yyyy, hh:mm:ss a')
                                  : 'N/A'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {order.basketOrder ? order.stockName : order.stockId ?? '--'}
                              </Typography>
                              {order.basketOrder && (
                                <div style={{ marginRight: '3px' }}>
                                  <ShoppingCart size={20} />
                                </div>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                              <Stack spacing={0.5} sx={{ flex: 1 }}>
                                {!order.basketOrder && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                    QTY: {order?.quantity ?? 0} | Price:&nbsp;
                                    <Amount amount={order.price} variant="subtitle2" symbol="₹" />
                                  </Typography>
                                )}
                                {!order.basketOrder && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                    LTP:&nbsp;
                                    {order.stockId && liveTicks.current[order.stockId]?.price != null ? (
                                      <Amount amount={liveTicks.current[order.stockId].price} variant="subtitle2" symbol="₹" />
                                    ) : (
                                      <Typography variant="subtitle2" component="span">
                                        --
                                      </Typography>
                                    )}
                                  </Typography>
                                )}
                                {order.insufficientFunds && (
                                  <Typography
                                    variant="caption"
                                    sx={{ pl: 0.75, pr: 0.75, py: 0.25, borderRadius: 1, display: 'inline-block', bgcolor: 'error.lighter', color: 'error.dark', fontWeight: 600 }}
                                  >
                                    ⚠ Insufficient balance — order could not be placed
                                  </Typography>
                                )}
                                {order.reasons && order.reasons.length > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Reason: {order.reasons.join(', ')}
                                  </Typography>
                                )}
                                {!order.insufficientFunds && order.orderRequestStatus === OrderRequestStatus.OPEN && (() => {
                                  const isOptionsOrder =
                                    order.option === true ||
                                    (order.basketOrder && order.orderRequestList?.every((r) => r.option === true));
                                  if (isOptionsOrder) return null;
                                  // For SELL orders, only show the toggle if it's an MTF order
                                  if (order.transactionType === 'SELL' && order.product !== 'MTF') return null;
                                  return (
                                  <Stack spacing={0.25}>
                                    <FormControlLabel
                                      sx={{ m: 0 }}
                                      control={
                                        <Switch
                                          size="small"
                                          checked={mtfToggle[order.requestId] ?? false}
                                          onChange={(e) =>
                                            setMtfToggle((prev) => ({ ...prev, [order.requestId]: e.target.checked }))
                                          }
                                          color="primary"
                                          disabled={order.transactionType !== 'SELL' && !order.accountUseMTF}
                                        />
                                      }
                                      label={
                                        <Typography variant="caption">
                                          {order.transactionType === 'SELL' ? 'Sell from MTF position' : 'Proceed with MTF'}
                                        </Typography>
                                      }
                                    />
                                    {order.transactionType !== 'SELL' && !order.accountUseMTF && (
                                      <Typography
                                        variant="caption"
                                        sx={{ pl: 0.75, pr: 0.75, py: 0.25, borderRadius: 1, display: 'inline-block', bgcolor: 'error.lighter', color: 'error.dark', fontWeight: 600 }}
                                      >
                                        ⚠ MTF is not enabled in this account. Enable MTF in the user profile to proceed with MTF.
                                      </Typography>
                                    )}
                                    {order.transactionType !== 'SELL' && order.accountUseMTF && order.product === 'MTF' && !mtfToggle[order.requestId] && (
                                      <Typography
                                        variant="caption"
                                        sx={{ pl: 0.75, pr: 0.75, py: 0.25, borderRadius: 1, display: 'inline-block', bgcolor: 'warning.lighter', color: 'warning.dark', fontWeight: 600 }}
                                      >
                                        ⚠ This order is suggested as MTF. Enable &quot;Proceed with MTF&quot; to execute it as intended.
                                      </Typography>
                                    )}
                                    {order.transactionType === 'BUY' && (order?.requiredMargin > 0 || (order?.price > 0 && order?.quantity > 0)) && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          pl: 0.75,
                                          pr: 0.75,
                                          py: 0.25,
                                          borderRadius: 1,
                                          display: 'inline-block',
                                          ...(mtfToggle[order.requestId]
                                            ? { bgcolor: 'warning.lighter', color: 'warning.dark', fontWeight: 600 }
                                            : { color: 'text.secondary' })
                                        }}
                                      >
                                        {mtfToggle[order.requestId]
                                          ? `Margin Required: ${order?.requiredMargin > 0 ? formatNumberToCurrency(order?.requiredMargin, true, CurrencyType.INR) : '...'}`
                                          : order?.price > 0
                                            ? `Amount Required: ${formatNumberToCurrency(order.price * order.quantity, true, CurrencyType.INR)}`
                                            : `Margin Required: ${formatNumberToCurrency(order?.requiredMargin, true, CurrencyType.INR)}`}
                                      </Typography>
                                    )}
                                  </Stack>
                                  );
                                })()}

                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                  Broker:&nbsp;
                                  {order.brokers}
                                  &nbsp;| &nbsp;Strategy: {order?.scenarioDisplayName ?? '_'}
                                </Typography>
                                {order.basketOrder && (
                                  <Box>
                                    {order?.orderRequestList.map((r, i) => (
                                      <Typography
                                        key={i}
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                      >
                                        <Typography
                                          variant="caption"
                                          color={r.transactionType === 'BUY' ? 'primary' : 'error'}
                                          sx={{ fontWeight: 'bold', mr: 1 }}
                                        >
                                          {r.transactionType}
                                        </Typography>

                                        {r.option === false && (
                                          <Typography variant="caption">
                                            {r.quantity} x {r.stockId} @ {r.price} (LTP:{' '}
                                            {r.stockId && liveTicks.current[r.stockId]?.price != null
                                              ? liveTicks.current[r.stockId].price
                                              : '--'}
                                            )
                                          </Typography>
                                        )}

                                        {r.option === true && (
                                          <Typography variant="caption">
                                            {r.quantity} x {r.stockId} {r.strikePrice} {r.optionType} @ {r.price}
                                          </Typography>
                                        )}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}

                                {/* <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                  Stoploss:&nbsp;
                                  <Amount amount={order.stoploss} variant="subtitle2" symbol="₹" />
                                  &nbsp;| Trigger Price:&nbsp;
                                  <Amount amount={order.triggerPrice} variant="subtitle2" symbol="₹" />
                                </Typography> */}
                              </Stack>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                              >
                                <Typography
                                  variant="caption"
                                  color={order.transactionType === 'BUY' ? 'primary' : 'error'}
                                  sx={{ ml: 4, mb: 1, mt: -4, fontWeight: 'bold' }}
                                >
                                  {order.transactionType}
                                </Typography>
                                <Grid sx={{ mt: 5 }}>
                                  {order.orderRequestStatus === OrderRequestStatus.OPEN ? (
                                    hasApproveOrderPermission && (
                                      <>
                                        <IconButton
                                          color="error"
                                          aria-label="cancel"
                                          size="medium"
                                          sx={{ p: 0.5 }}
                                          onClick={() =>
                                            handleDeclineOrder(order?.requestId.toString(), order.orderId.toString(), order.userId)
                                          }
                                        >
                                          <CancelIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          color="info"
                                          aria-label="edit"
                                          size="medium"
                                          sx={{ p: 0.5 }}
                                          onClick={() => {
                                            let quantity = order.quantity;

                                            const isBasketOrder = order.basketOrder === true;

                                            if (
                                              isBasketOrder &&
                                              Array.isArray(order.orderRequestList) &&
                                              order.orderRequestList.length > 0
                                            ) {
                                              quantity = order.orderRequestList[0].quantity;
                                            }

                                            setChangeQtyState({
                                              open: true,
                                              orderId: order.requestId?.toString() ?? '',
                                              userId: order.userId ?? '',
                                              currentQty: quantity ?? 0
                                            });
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          color="success"
                                          aria-label="approve"
                                          size="medium"
                                          sx={{ p: 0.5 }}
                                          onClick={() => handleApproveOrder(order.requestId.toString(), mtfToggle[order.requestId] ?? false)}
                                        >
                                          <CheckCircleIcon fontSize="small" />
                                        </IconButton>
                                      </>
                                    )
                                  ) : (
                                    <Chip
                                      label={order.orderRequestStatus}
                                      color={
                                        order.orderRequestStatus === OrderRequestStatus.CANCELLED ||
                                        order.orderRequestStatus === OrderRequestStatus.REJECTED
                                          ? 'error'
                                          : 'success'
                                      }
                                      size="small"
                                      variant="light"
                                    />
                                  )}
                                </Grid>
                              </Stack>
                            </Box>
                          </ListItem>
                        ))}
                    </List>
                  )}
                </Grid>
              </Box>
            </SimpleBar>
          </MainCard>
        )}
      </Drawer>
    </>
  );
};

export default OrderRequestDrawer;
